import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { PublicHeader } from "@/components/PublicHeader";
import { PublicFooter } from "@/components/PublicFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";

const EMPTY = { name: "", company: "", work_email: "", title: "", team_size: "", what_they_sell: "", note: "" };

export default function Demo() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.work_email) return toast.error("Name, company and work email are required");
    setBusy(true);
    try { await api.post("/demo-request", form); setDone(true); }
    catch (err) { const d = err?.response?.data?.detail; toast.error((d && (d.message || d)) || "Could not submit request"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PublicHeader />
      <section className="max-w-lg mx-auto px-5 sm:px-8 py-16 sm:py-24">
        {done ? (
          <div className="text-center rounded-xl border border-border/70 bg-card/50 p-10" data-testid="demo-thank-you">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
            <h1 className="font-display text-2xl font-semibold">Thanks — we'll be in touch</h1>
            <p className="text-sm text-muted-foreground mt-2">We will reply from DealsOps to schedule your demo.</p>
          </div>
        ) : (
          <>
            <div className="eyebrow mb-3">Request a demo</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">See DealsOps live</h1>
            <p className="mt-3 text-sm text-muted-foreground">Use your work email. There is no free plan or trial — demos are scheduled.</p>
            <form onSubmit={submit} className="mt-8 space-y-3" data-testid="demo-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={form.name} onChange={set("name")} className="mt-1.5" data-testid="demo-name" /></div>
                <div><Label>Company</Label><Input value={form.company} onChange={set("company")} className="mt-1.5" data-testid="demo-company" /></div>
              </div>
              <div><Label>Work email</Label><Input value={form.work_email} onChange={set("work_email")} className="mt-1.5" placeholder="you@company.com" data-testid="demo-email" /></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Title</Label><Input value={form.title} onChange={set("title")} className="mt-1.5" data-testid="demo-title" /></div>
                <div><Label>Team size</Label><Input value={form.team_size} onChange={set("team_size")} className="mt-1.5" placeholder="e.g. 10-50" data-testid="demo-team-size" /></div>
              </div>
              <div><Label>What do you sell?</Label><Input value={form.what_they_sell} onChange={set("what_they_sell")} className="mt-1.5" data-testid="demo-sell" /></div>
              <div><Label>Note (optional)</Label><Textarea value={form.note} onChange={set("note")} className="mt-1.5" rows={2} data-testid="demo-note" /></div>
              <Button type="submit" disabled={busy} className="rounded-md bg-amber-500 text-black hover:bg-amber-400 min-h-[44px] w-full" data-testid="demo-submit-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request a demo"}
              </Button>
            </form>
          </>
        )}
      </section>
      <PublicFooter />
    </div>
  );
}
